/**
 * Checkbox component with label support.
 *
 * @module components/ui/Checkbox
 */

import { forwardRef, InputHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label text */
  label?: string;
  /** Helper text shown below the checkbox */
  helperText?: string;
}

/**
 * Checkbox component with optional label and helper text
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, helperText, className, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            ref={ref}
            type="checkbox"
            id={inputId}
            className={clsx(
              'mt-0.5 h-4 w-4 rounded-sm border-gray-300 dark:border-[#2a2a2a]',
              'text-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-0',
              'dark:bg-[#161616] dark:checked:bg-primary-600',
              'transition-colors duration-200 cursor-pointer',
              className
            )}
            {...props}
          />
          {label && (
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
          )}
        </label>
        {helperText && (
          <p className="mt-1.5 ml-7 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
